import { BaseElement, defineElement } from '@webtides/element-js';

/**
 * @property {'default' | 'highlight'} variant
 * @property {boolean} selected
 * @property {string} linkSelector
 * @property {string} panelSelector
 */
export default class TabGroup extends BaseElement {
    tabLinks = [];
    tabPanels = [];

    constructor() {
        super({ propertyOptions: { selected: { reflect: true } } });
    }

    properties() {
        return {
            variant: 'default',
            linkSelector: 'tab-link',
            panelSelector: 'tab-panel',
            selected: undefined,
        };
    }

    watch() {
        return {
            selected: (selected) => {
                window.location.hash = `#${selected}`;
                this.updateSelection();
            },
        };
    }

    connected() {
        this.tabLinks = this.queryTabLinks();
        this.tabPanels = this.queryTabPanels();

        // check if tab was selected via url hash
        if (window.location.hash) {
            this.selectTab(window.location.hash.replace('#', ''));
        }

        // select first tab if none was selected declarative
        if (!this.selected && this.tabPanels.length > 0) {
            const firstPanel = this.tabPanels[0];
            this.selectTab(firstPanel.name);
        }

        customElements.whenDefined('tab-link').then(() => {
            this.updateSelection();
        });

        // Accessibility
        this.setAttribute('role', 'tablist');
    }

    events() {
        return {
            this: {
                'tab-select': (event) => {
                    const { selected } = event.detail;
                    this.selected = selected;
                },
                keydown: (event) => {
                    // Move focus left or right
                    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
                        const activeEl = Array.from(this.tabLinks).find((t) => t.matches(':focus'));

                        if (!activeEl) return;

                        let index = Array.from(this.tabLinks).indexOf(activeEl);

                        if (event.key === 'Home') {
                            index = 0;
                        } else if (event.key === 'End') {
                            index = this.tabLinks.length - 1;
                        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                            index--;
                        } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                            index++;
                        }

                        if (index < 0) {
                            index = this.tabLinks.length - 1;
                        }

                        if (index > this.tabLinks.length - 1) {
                            index = 0;
                        }

                        this.tabLinks[index].focus({ preventScroll: true });

                        event.preventDefault();
                    }
                },
            },
            window: {
                hashchange: () => {
                    this.selectTab(window.location.hash.replace('#', ''));
                },
            },
        };
    }

    selectTab(name) {
        // TODO: check if id is actually possible?
        this.selected = name;
    }

    updateSelection() {
        for (const tabLink of this.tabLinks) {
            const panelName = tabLink.getAttribute('for');
            tabLink.selected = panelName === this.selected;

            if (panelName === this.selected) {
                const distance = tabLink.previousElementSibling ? tabLink.getBoundingClientRect().left - tabLink.parentElement.getBoundingClientRect().left - 20 : 0;
                tabLink.parentElement.scrollTo({
                    top: 0,
                    left: distance,
                    behavior: 'smooth',
                });
            }
        }

        for (const tabPanel of this.tabPanels) {
            const panelName = tabPanel.getAttribute('name');
            tabPanel.style['display'] = panelName === this.selected ? 'block' : 'none';
        }
    }

    queryTabLinks() {
        return this.querySelectorAll(this.linkSelector);
    }

    queryTabPanels() {
        return this.querySelectorAll(this.panelSelector);
    }
}

defineElement('tab-group', TabGroup);
